/**
 * Hook för SystemPackagesManager
 * Hanterar state och logik för CRM Prislista - systempaket och modulpriser
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import api from '@/services/api/client';
import { useTranslation } from '@/store/languageStore';
import type { MenuSystem, UserMenuResponse } from '@/types/systems';
import type {
  SystemWithPackages,
  Module,
  ModuleWithPricing,
  AvailableWidget,
  SystemPackage,
  PackageFormData,
  PricingModel,
} from '@/types/systemPackages';

const initialFormData: PackageFormData = {
  name: '',
  displayName: '',
  description: '',
  priceMonthly: 0,
  priceYearly: 0,
  isDefault: false,
  maxUsers: '',
  maxVehicles: '',
  features: '',
  moduleIds: [],
};

export function useSystemPackagesManager() {
  const { t } = useTranslation();
  
  // Data state
  const [systems, setSystems] = useState<SystemWithPackages[]>([]);
  const [_allModules, setAllModules] = useState<Module[]>([]);
  const [modulesWithPricing, setModulesWithPricing] = useState<ModuleWithPricing[]>([]);
  const [menuSystems, setMenuSystems] = useState<MenuSystem[]>([]);
  const [availableWidgets, setAvailableWidgets] = useState<AvailableWidget[]>([]);
  
  // UI state
  const [expandedSystems, setExpandedSystems] = useState<Set<string>>(new Set());
  const [expandedMenuItems, setExpandedMenuItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<SystemPackage | null>(null);
  const [selectedSystemId, setSelectedSystemId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'packages' | 'modules'>('packages');
  
  // Module price editing state
  const [editingModulePrice, setEditingModulePrice] = useState<string | null>(null);
  const [modulePriceInput, setModulePriceInput] = useState<string>('');
  const [pricingModelInput, setPricingModelInput] = useState<PricingModel>('monthly');
  
  // Form state
  const [formData, setFormData] = useState<PackageFormData>(initialFormData);

  // Load all data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [packagesRes, modulesRes, modulesPricingRes, menuRes, widgetsRes] = await Promise.all([
        api.get<{ success: boolean; data: SystemWithPackages[] }>('/api/packages/systems'),
        api.get<{ success: boolean; data: any }>('/api/systems/with-modules'),
        api.get<{ success: boolean; data: ModuleWithPricing[] }>('/api/packages/modules'),
        api.get<{ success: boolean; data: UserMenuResponse }>('/api/menu'),
        api.get<{ success: boolean; data: AvailableWidget[] }>('/api/dashboard/widgets/available'),
      ]);
      
      if (packagesRes.data.success) {
        setSystems(packagesRes.data.data);
        setExpandedSystems(new Set(packagesRes.data.data.map(s => s.id)));
      }
      
      if (modulesRes.data.success && modulesRes.data.data) {
        const modules: Module[] = [];
        
        modulesRes.data.data.forEach((s: any) => {
          s.modules?.forEach((m: any) => {
            modules.push({ ...m, systemId: s.id });
          });
        });
        
        setAllModules(modules);
      }

      if (modulesPricingRes.data.success) {
        setModulesWithPricing(modulesPricingRes.data.data);
      }

      if (menuRes.data.success && menuRes.data.data) {
        setMenuSystems(menuRes.data.data.systems);
        setExpandedMenuItems(new Set());
      }

      if (widgetsRes.data.success) {
        setAvailableWidgets(widgetsRes.data.data);
      }
    } catch (error) {
      console.error('Error loading packages:', error);
      toast.error(t('packages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleSaveModulePrice = useCallback(async (moduleId: string, pricingModel: PricingModel) => {
    try {
      const priceValue = parseFloat(modulePriceInput.replace(',', '.')) || 0;
      
      await api.put(`/api/packages/modules/${moduleId}/price`, {
        priceMonthly: priceValue,
        priceYearly: priceValue * 10,
        pricingModel,
      });
      toast.success(t('packages.modulePriceSaved'));
      setEditingModulePrice(null);
      await loadData();
    } catch (error) {
      console.error('Error saving module price:', error);
      toast.error(t('packages.modulePriceError'));
    }
  }, [modulePriceInput, loadData, t]);

  const toggleExpand = useCallback((systemId: string) => {
    setExpandedSystems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(systemId)) {
        newSet.delete(systemId);
      } else {
        newSet.add(systemId);
      }
      return newSet;
    });
  }, []);

  const toggleMenuItem = useCallback((itemId: string) => {
    setExpandedMenuItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  const openCreateModal = useCallback((systemId: string) => {
    setSelectedSystemId(systemId);
    setEditingPackage(null);
    setFormData(initialFormData);
    setShowModal(true);
  }, []);

  const openEditModal = useCallback((pkg: SystemPackage) => {
    setSelectedSystemId(pkg.systemId);
    setEditingPackage(pkg);
    
    const moduleIds = pkg.modules?.filter(m => m.isIncluded).map(m => m.id) || [];
    
    setFormData({
      name: pkg.name,
      displayName: pkg.displayName,
      description: pkg.description || '',
      priceMonthly: pkg.priceMonthly,
      priceYearly: pkg.priceYearly,
      isDefault: pkg.isDefault,
      maxUsers: pkg.maxUsers?.toString() || '',
      maxVehicles: pkg.maxVehicles?.toString() || '',
      features: pkg.features?.join('\n') || '',
      moduleIds,
    });
    setShowModal(true);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      const payload = {
        systemId: selectedSystemId,
        name: formData.name,
        displayName: formData.displayName,
        description: formData.description || undefined,
        priceMonthly: formData.priceMonthly,
        priceYearly: formData.priceYearly || formData.priceMonthly * 10,
        isDefault: formData.isDefault,
        maxUsers: formData.maxUsers ? parseInt(formData.maxUsers) : undefined,
        maxVehicles: formData.maxVehicles ? parseInt(formData.maxVehicles) : undefined,
        features: formData.features ? formData.features.split('\n').filter(f => f.trim()) : undefined,
        moduleIds: formData.moduleIds,
      };

      if (editingPackage) {
        await api.put(`/api/packages/${editingPackage.id}`, payload);
        await api.put(`/api/packages/${editingPackage.id}/modules`, { moduleIds: formData.moduleIds });
        toast.success(t('packages.packageUpdated'));
      } else {
        await api.post('/api/packages', payload);
        toast.success(t('packages.packageCreated'));
      }

      setShowModal(false);
      await loadData();
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error(t('packages.saveError'));
    }
  }, [selectedSystemId, formData, editingPackage, loadData, t]);

  const toggleModule = useCallback((moduleId: string) => {
    setFormData(prev => ({
      ...prev,
      moduleIds: prev.moduleIds.includes(moduleId)
        ? prev.moduleIds.filter(id => id !== moduleId)
        : [...prev.moduleIds, moduleId],
    }));
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingPackage(null);
  }, []);

  // Get purchasable modules for selected system in package modal
  const systemModules = useMemo(() => {
    // Paketredigering ska kunna välja alla relevanta moduler i vänstermenyn,
    // inte bara moduler från det valda systemet.
    const filtered = modulesWithPricing.filter((m) => {
      if (!m.id || !m.displayName) return false;

      const sysName = String(m.systemName || '').toLowerCase();
      const sysDisplayName = String(m.systemDisplayName || '').toLowerCase();
      if (sysName === 'sysadmin' || sysDisplayName === 'sysadmin') return false;
      if (sysName === 'crm' || sysDisplayName === 'crm') return false;

      return true;
    });

    // Defensive de-duplication for legacy data inconsistencies:
    // use name/route/displayName identity inside same system.
    // Note: name is preferred since multiple modules can share the same route (e.g. dashboard widgets).
    const unique = new Map<string, (typeof filtered)[number]>();
    for (const module of filtered) {
      const identity = (
        module.name ||
        module.route ||
        module.displayName
      ).toLowerCase();
      const key = `${String(module.systemId)}:${identity}`;
      if (!unique.has(key)) {
        unique.set(key, module);
      }
    }

    return Array.from(unique.values())
      .map(m => ({
        id: m.id,
        name: m.name,
        displayName: m.name?.startsWith('widget-')
          ? `Widget: ${m.displayName}`
          : m.name?.startsWith('mobile-')
            ? `Mobil: ${m.displayName}`
            : `${m.systemDisplayName || 'Övrigt'}: ${m.displayName}`,
        icon: m.icon,
        systemId: m.systemId,
        systemName: m.systemDisplayName || 'Övrigt',
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName, 'sv'));
  }, [modulesWithPricing]);

  // Collect all module IDs that are included in any package
  const modulesInPackages = useMemo(() => {
    const moduleIds = new Set<string>();
    systems.forEach(sys => {
      sys.packages.forEach(pkg => {
        pkg.modules?.forEach(m => {
          if (m.isIncluded) {
            moduleIds.add(m.id);
          }
        });
      });
    });
    return moduleIds;
  }, [systems]);

  // Find modules that are NOT in any package (excluding SysAdmin and CRM modules)
  const modulesWithoutPackages = useMemo(() => {
    return modulesWithPricing.filter(m => {
      const sysName = (m.systemName || '').toLowerCase();
      const sysDisplayName = (m.systemDisplayName || '').toLowerCase();
      
      if (sysName === 'sysadmin' || sysDisplayName === 'sysadmin') {
        return false;
      }
      if (sysName === 'crm' || sysDisplayName === 'crm') {
        return false;
      }
      return !modulesInPackages.has(m.id);
    });
  }, [modulesWithPricing, modulesInPackages]);

  // Group modules without packages by system for better overview
  const modulesWithoutPackagesBySystem = useMemo(() => {
    return modulesWithoutPackages.reduce((acc, m) => {
      const systemName = m.systemDisplayName || m.systemName || 'Övrigt';
      if (!acc[systemName]) {
        acc[systemName] = [];
      }
      acc[systemName].push(m);
      return acc;
    }, {} as Record<string, ModuleWithPricing[]>);
  }, [modulesWithoutPackages]);

  // Start editing a module price
  const startEditModulePrice = useCallback((moduleId: string, currentPrice: number, currentModel: PricingModel) => {
    setEditingModulePrice(moduleId);
    setModulePriceInput(currentPrice.toString());
    setPricingModelInput(currentModel);
  }, []);

  const cancelEditModulePrice = useCallback(() => {
    setEditingModulePrice(null);
  }, []);

  return {
    // Data
    systems,
    modulesWithPricing,
    menuSystems,
    availableWidgets,
    systemModules,
    modulesWithoutPackagesBySystem,
    
    // UI State
    loading,
    expandedSystems,
    expandedMenuItems,
    showModal,
    editingPackage,
    selectedSystemId,
    activeTab,
    
    // Module price editing
    editingModulePrice,
    modulePriceInput,
    pricingModelInput,
    
    // Form
    formData,
    
    // Setters
    setActiveTab,
    setFormData,
    setModulePriceInput,
    setPricingModelInput,
    
    // Actions
    loadData,
    toggleExpand,
    toggleMenuItem,
    openCreateModal,
    openEditModal,
    closeModal,
    handleSave,
    handleSaveModulePrice,
    toggleModule,
    startEditModulePrice,
    cancelEditModulePrice,
  };
}
